import { useEffect, useState } from "react";
import { Redirect, useLocation } from "wouter";
import {
  addVat,
  SUBSCRIPTION_TIERS,
  TIER_CATALOG,
  TIER_FEATURE_MATRIX,
  VAT_RATE,
  type BillingCycle,
  type SubscriptionTier,
  type TenantPlansResponse,
  type TenantPlanChangeRequestDto,
  type TierOverQuotaItem,
} from "@orbit/api-zod";
import { getMerchantToken, merchantFetch } from "../lib/merchantAuth";
import { MerchantNav } from "../components/MerchantNav";

/**
 * Phase 34 (D107) — merchant Plans page.
 *
 * Three tier cards from `TIER_CATALOG` (single source of truth shared
 * with the admin queue). Current tier is highlighted. Pending requests
 * collapse the request form into a status panel with a Cancel button.
 *
 * Downgrade-over-quota is intentionally a soft warning: per task spec,
 * approved downgrades take effect immediately and ops handles the
 * over-quota fallout manually. We surface a friendly banner here so
 * the merchant knows what to expect.
 */

const TIER_ORDER: Record<SubscriptionTier, number> = {
  FREE: 0,
  PRO: 1,
  ELITE: 2,
};

/**
 * Catalog prices are display strings like "₱1,499" / "₱14,990".
 * Strip the symbol/commas to get a Number we can multiply by
 * (1 + VAT_RATE). Returns null for non-numeric ("Free", etc.) so
 * the caller can suppress the "incl VAT" line for FREE.
 */
function parsePesoLabel(label: string): number | null {
  const digits = label.replace(/[^0-9.]/g, "");
  if (!digits) return null;
  const n = Number.parseFloat(digits);
  return Number.isFinite(n) && n > 0 ? n : null;
}
function withVatLabel(label: string): string | null {
  const base = parsePesoLabel(label);
  if (base === null) return null;
  return `≈ ₱${addVat(base).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} incl. ${Math.round(VAT_RATE * 100)}% VAT`;
}

export function MerchantPlansPage() {
  const [, navigate] = useLocation();
  const [snapshot, setSnapshot] = useState<TenantPlansResponse | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  // Request form state
  const [targetTier, setTargetTier] = useState<SubscriptionTier | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("MONTHLY");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await merchantFetch<TenantPlansResponse>("/api/v1/tenant/plans");
      if (cancelled) return;
      if (!r.ok) {
        if (r.status === 401) {
          navigate("/merchant/login");
          return;
        }
        setLoadErr(r.message);
        return;
      }
      setSnapshot(r.data);
      setLoadErr(null);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, reloadTick]);

  if (!getMerchantToken()) return <Redirect to="/merchant/login" />;

  const pending: TenantPlanChangeRequestDto | null =
    snapshot?.pendingRequest ?? null;

  async function submitRequest(e: React.FormEvent) {
    e.preventDefault();
    if (!snapshot || !targetTier) return;
    setSubmitErr(null);
    setSubmitting(true);
    const r = await merchantFetch<{ request: TenantPlanChangeRequestDto }>(
      "/api/v1/tenant/plans/request-change",
      {
        method: "POST",
        body: JSON.stringify({
          requestedTier: targetTier,
          billingCycle,
          note: note.trim() || undefined,
        }),
      },
    );
    setSubmitting(false);
    if (!r.ok) {
      setSubmitErr(r.message);
      return;
    }
    setTargetTier(null);
    setNote("");
    setReloadTick((n) => n + 1);
  }

  async function cancelPending() {
    if (!pending) return;
    setCancelBusy(true);
    const r = await merchantFetch<{ request: TenantPlanChangeRequestDto }>(
      `/api/v1/tenant/plans/request-change/${encodeURIComponent(pending.id)}`,
      { method: "DELETE" },
    );
    setCancelBusy(false);
    if (!r.ok) {
      setSubmitErr(r.message);
      return;
    }
    setReloadTick((n) => n + 1);
  }

  return (
    <div className="min-h-screen pb-12">
      <div className="mx-auto max-w-4xl px-6 pt-8">
        <MerchantNav current="plans" />

        <h1 className="mt-6 text-2xl font-bold text-navy">Plans &amp; pricing</h1>
        <p className="mt-1 text-sm text-ink-dark">
          Choose the plan that fits where your business is going. Upgrade
          or downgrade requests are reviewed by our team within one
          business day after off-platform payment is confirmed.{" "}
          <span className="font-medium text-navy">
            Prices shown are VAT-exclusive — {Math.round(VAT_RATE * 100)}%
            VAT is added to the final invoice.
          </span>
        </p>

        {loadErr && (
          <div className="mt-4 rounded-card border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {loadErr}
          </div>
        )}

        {snapshot && snapshot.overQuota.length > 0 && (
          <OverQuotaPanel
            tierName={TIER_CATALOG[snapshot.currentTier].name}
            items={snapshot.overQuota}
          />
        )}

        {snapshot && pending && (
          <PendingPanel
            pending={pending}
            onCancel={cancelPending}
            cancelBusy={cancelBusy}
            error={submitErr}
          />
        )}

        {snapshot && (
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {SUBSCRIPTION_TIERS.map((tier) => {
              const entry = TIER_CATALOG[tier];
              const isCurrent = snapshot.currentTier === tier;
              return (
                <article
                  key={tier}
                  className={`rounded-card border p-5 ${
                    isCurrent
                      ? "border-navy bg-amber-light/30 shadow-sm"
                      : "border-amber-light bg-white"
                  }`}
                >
                  <div className="flex items-baseline justify-between">
                    <h2 className="text-lg font-bold text-navy">
                      {entry.name}
                    </h2>
                    {isCurrent && (
                      <span className="rounded-full bg-navy px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-ink-dark">{entry.tagline}</p>
                  <p className="mt-3 font-mono text-xl text-navy">
                    {entry.monthlyPricePhp}
                    <span className="ml-1 text-xs font-normal text-ink-dark">
                      / month
                    </span>
                  </p>
                  {withVatLabel(entry.monthlyPricePhp) && (
                    <p className="text-[11px] italic text-ink-dark">
                      {withVatLabel(entry.monthlyPricePhp)}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-ink-dark">
                    or {entry.yearlyPricePhp} / year
                    {withVatLabel(entry.yearlyPricePhp) && (
                      <span className="block italic text-[11px]">
                        {withVatLabel(entry.yearlyPricePhp)}
                      </span>
                    )}
                  </p>
                  <ul className="mt-3 space-y-1 text-sm text-navy">
                    {entry.highlights.map((h) => (
                      <li key={h}>• {h}</li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        )}

        {snapshot && (
          <section className="mt-8 rounded-card border border-amber-light bg-white p-4">
            <h2 className="text-sm font-semibold text-navy">Feature comparison</h2>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-ink-dark">
                    <th className="py-2 pr-3"></th>
                    {SUBSCRIPTION_TIERS.map((t) => (
                      <th key={t} className="py-2 pr-3">
                        {TIER_CATALOG[t].name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-light/60">
                  {TIER_FEATURE_MATRIX.map((row) => (
                    <tr key={row.label}>
                      <td className="py-2 pr-3 text-ink-dark">{row.label}</td>
                      {SUBSCRIPTION_TIERS.map((t) => (
                        <td key={t} className="py-2 pr-3 font-mono text-navy">
                          {row.values[t]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {snapshot && !pending && (
          <section className="mt-8 rounded-card border border-amber-light bg-white p-4">
            <h2 className="text-sm font-semibold text-navy">
              Request a plan change
            </h2>
            <form onSubmit={submitRequest} className="mt-3 space-y-3">
              <div>
                <label className="label" htmlFor="target-tier">
                  Target tier
                </label>
                <select
                  id="target-tier"
                  className="input"
                  value={targetTier ?? ""}
                  onChange={(e) =>
                    setTargetTier(
                      (e.target.value || null) as SubscriptionTier | null,
                    )
                  }
                  required
                >
                  <option value="">Choose a tier…</option>
                  {SUBSCRIPTION_TIERS.filter(
                    (t) => t !== snapshot.currentTier,
                  ).map((t) => {
                    const direction =
                      TIER_ORDER[t] > TIER_ORDER[snapshot.currentTier]
                        ? "Upgrade"
                        : "Downgrade";
                    return (
                      <option key={t} value={t}>
                        {direction} → {TIER_CATALOG[t].name}
                      </option>
                    );
                  })}
                </select>
              </div>

              {targetTier &&
                TIER_ORDER[targetTier] <
                  TIER_ORDER[snapshot.currentTier] && (
                  <div className="rounded-card border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    Heads up: downgrades take effect immediately on
                    approval. If your current usage is over the new tier's
                    limits, your account stays active but new transactions
                    above the cap may be blocked. Reach out if you need
                    help reducing usage first.
                  </div>
                )}

              <fieldset>
                <legend className="label">Billing cycle</legend>
                <div className="mt-1 flex gap-4 text-sm">
                  {(["MONTHLY", "YEARLY"] as const).map((c) => (
                    <label key={c} className="flex items-center gap-1">
                      <input
                        type="radio"
                        name="billing-cycle"
                        value={c}
                        checked={billingCycle === c}
                        onChange={() => setBillingCycle(c)}
                      />
                      {c === "MONTHLY" ? "Monthly" : "Yearly (save ~15%)"}
                    </label>
                  ))}
                </div>
              </fieldset>

              <div>
                <label className="label" htmlFor="note">
                  Note (optional)
                </label>
                <textarea
                  id="note"
                  className="input"
                  rows={3}
                  maxLength={1000}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Tell us about your business or any timing constraints…"
                />
              </div>

              {submitErr && (
                <p className="text-sm text-red-700">{submitErr}</p>
              )}

              <button
                type="submit"
                disabled={submitting || !targetTier}
                className="btn-primary"
              >
                {submitting ? "Submitting…" : "Submit request"}
              </button>
            </form>
          </section>
        )}
      </div>
    </div>
  );
}

function OverQuotaPanel({
  tierName,
  items,
}: {
  tierName: string;
  items: TierOverQuotaItem[];
}) {
  return (
    <section className="mt-6 rounded-card border border-red-300 bg-red-50 p-4">
      <h2 className="text-sm font-semibold text-red-800">
        You're over your {tierName} plan limits
      </h2>
      <p className="mt-1 text-sm text-red-900">
        Your account stays active, but new activity above the cap may
        be blocked. Reduce usage to clear this banner, or upgrade to a
        higher plan below.
      </p>
      <ul className="mt-2 space-y-1 text-sm text-red-900">
        {items.map((it) => (
          <li key={it.metric}>
            • <span className="font-semibold">{it.label}:</span>{" "}
            <span className="font-mono">{it.current.toLocaleString()}</span>{" "}
            / {it.limit.toLocaleString()} (please reduce by{" "}
            {(it.current - it.limit).toLocaleString()})
          </li>
        ))}
      </ul>
    </section>
  );
}

function PendingPanel({
  pending,
  onCancel,
  cancelBusy,
  error,
}: {
  pending: TenantPlanChangeRequestDto;
  onCancel: () => void;
  cancelBusy: boolean;
  error: string | null;
}) {
  const targetEntry = TIER_CATALOG[pending.requestedTier];
  const targetName = targetEntry.name;
  const priceLabel =
    pending.billingCycle === "YEARLY"
      ? targetEntry.yearlyPricePhp
      : targetEntry.monthlyPricePhp;
  const finalLabel = withVatLabel(priceLabel);
  return (
    <section className="mt-6 rounded-card border border-amber-300 bg-amber-50 p-4">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-navy">
            Request submitted —{" "}
            {pending.currentTier} → {pending.requestedTier} ({targetName},{" "}
            {pending.billingCycle.toLowerCase()})
          </h2>
          <p className="mt-1 text-sm text-ink-dark">
            We'll confirm within one business day after off-platform
            payment is received. You can cancel any time before approval.
          </p>
          {finalLabel && (
            <p className="mt-2 text-sm text-navy">
              <span className="font-semibold">Amount due:</span> {priceLabel}{" "}
              <span className="text-ink-dark">
                + {Math.round(VAT_RATE * 100)}% VAT ={" "}
                <span className="font-semibold text-navy">
                  ₱
                  {addVat(parsePesoLabel(priceLabel) ?? 0).toLocaleString(
                    undefined,
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                  )}
                </span>
              </span>
            </p>
          )}
          {pending.note && (
            <p className="mt-2 text-xs text-ink-dark">
              <span className="font-semibold">Your note:</span> {pending.note}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onCancel}
          disabled={cancelBusy}
          className="rounded-md border border-ink-mid bg-white px-3 py-1.5 text-xs font-medium text-navy hover:bg-amber-light/30 disabled:opacity-50"
        >
          {cancelBusy ? "Cancelling…" : "Cancel request"}
        </button>
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-700">{error}</p>
      )}
    </section>
  );
}
